/**
 * Gestor de Proyectos — tablero de tickets internos del equipo (BackOffice, solo SuperAdmin).
 *
 * Rebanada 2 (frontend) del módulo dev_tickets. Consume los 5 endpoints de R1
 * (/api/admin/dev-tickets); NO toca backend. El gate SuperAdminRoute se hereda de
 * la ruta padre /backoffice. Molde: pages/backoffice/AdTracking.js. Sin librerías nuevas.
 *
 * Estados de flujo (columnas): solicitado → aprobado → en_desarrollo → en_pruebas → desplegado.
 * Fuera de flujo (sección colapsable): rechazado / en_pausa / bloqueado.
 * Cambio de estado por SELECTOR en el detalle (no drag&drop): evita disparos
 * accidentales de Telegram y no requiere librerías nuevas.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { AlertTriangle, ChevronDown, ChevronRight, ClipboardList, Loader2, Lock, Plus, RefreshCw, Save, Send } from 'lucide-react';

// ---- Catálogos (labels de UI; no lógica de negocio) ----
const FLOW_STATUSES = ['solicitado', 'aprobado', 'en_desarrollo', 'en_pruebas', 'desplegado'];
const OFF_FLOW_STATUSES = ['rechazado', 'en_pausa', 'bloqueado'];
const ALL_STATUSES = [...FLOW_STATUSES, ...OFF_FLOW_STATUSES];
const STATUS_LABELS = {
  solicitado: 'Solicitado', aprobado: 'Aprobado', en_desarrollo: 'En desarrollo',
  en_pruebas: 'En pruebas', desplegado: 'Desplegado', rechazado: 'Rechazado', en_pausa: 'En pausa',
  bloqueado: 'Bloqueado',
};
const NOTIFY_STATUSES = ['aprobado', 'desplegado']; // disparan aviso Telegram al equipo (backend)

const TYPES = ['fix', 'update', 'new_module'];
const TYPE_LABELS = { fix: 'Fix', update: 'Update', new_module: 'Nuevo módulo' };

const PRIORITIES = ['urgente', 'alta', 'media', 'baja'];
const PRIORITY_LABELS = { urgente: 'Urgente', alta: 'Alta', media: 'Media', baja: 'Baja' };
const PRIORITY_RANK = { urgente: 0, alta: 1, media: 2, baja: 3 };

const VERTICALS = ['inmobiliaria', 'desarrollo', 'car_dealer', 'colegios', 'comercial'];
const VERTICAL_LABELS = {
  inmobiliaria: 'Inmobiliaria', desarrollo: 'Desarrollos', car_dealer: 'Automotriz',
  colegios: 'Colegios', comercial: 'Comercial',
};
const NONE = '__none__'; // centinela: Radix Select no admite value=""

// ---- Helpers ----
function typeBadgeClass(type) {
  return {
    fix: 'bg-rose-400/10 text-rose-300 border-rose-400/20',
    update: 'bg-sky-400/10 text-sky-300 border-sky-400/20',
    new_module: 'bg-violet-400/10 text-violet-300 border-violet-400/20',
  }[type] || 'bg-white/5 text-muted-foreground border-white/10';
}

function priorityBadgeClass(priority) {
  return {
    urgente: 'bg-red-500 text-white border-red-300 shadow-[0_0_0_2px_rgba(248,113,113,0.35)]',
    alta: 'bg-amber-400/15 text-amber-300 border-amber-400/30',
    media: 'bg-white/5 text-muted-foreground border-white/10',
    baja: 'bg-white/[0.03] text-white/40 border-white/10',
  }[priority] || 'bg-white/5 text-muted-foreground border-white/10';
}

function Pill({ className = '', children }) {
  return (
    <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${className}`}>
      {children}
    </span>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function SelectField({ value, onChange, options, labels, testid, allowNone = false, noneLabel = '—' }) {
  return (
    <Select value={value || (allowNone ? NONE : value)} onValueChange={v => onChange(v === NONE ? '' : v)}>
      <SelectTrigger className="bg-black/20 border-white/10 text-sm h-9" data-testid={testid}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {allowNone && <SelectItem value={NONE}>{noneLabel}</SelectItem>}
        {options.map(o => <SelectItem key={o} value={o}>{labels[o] || o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

// Selector de una entidad dinámica (companies / superadmins). `items` = [{value, label}].
// Si el `value` actual no está en `items` (empresa borrada, o responsable de texto viejo),
// lo conserva como opción sintética para no perderlo ni mostrar vacío.
function EntitySelect({ value, onChange, items, testid, placeholder = 'Selecciona…', currentLabel, noneLabel = '—' }) {
  const known = value && items.some(it => it.value === value);
  const allItems = (value && !known) ? [{ value, label: currentLabel || value }, ...items] : items;
  return (
    <Select value={value || NONE} onValueChange={v => onChange(v === NONE ? '' : v)}>
      <SelectTrigger className="bg-black/20 border-white/10 text-sm h-9" data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>{noneLabel}</SelectItem>
        {allItems.map(it => <SelectItem key={it.value} value={it.value}>{it.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function lastChangeAt(ticket) {
  const h = ticket.status_history || [];
  if (h.length) return h[h.length - 1].at || ticket.updated_at || ticket.created_at;
  return ticket.updated_at || ticket.created_at;
}

function affectedLabel(ticket) {
  const parts = [];
  if (ticket.affected_company_name) parts.push(ticket.affected_company_name);
  if (ticket.affected_vertical) parts.push(VERTICAL_LABELS[ticket.affected_vertical] || ticket.affected_vertical);
  return parts.join(' · ') || null;
}

// Motivo del bloqueo = note del ÚLTIMO evento `bloqueado` del historial (así, re-bloquear
// para actualizar el motivo se refleja solo). Vacío si el ticket no está bloqueado.
function blockReasonOf(ticket) {
  if (ticket.status !== 'bloqueado') return '';
  const h = ticket.status_history || [];
  for (let i = h.length - 1; i >= 0; i--) {
    if (h[i].status === 'bloqueado') return h[i].note || '';
  }
  return '';
}

function sortTickets(list) {
  return [...list].sort((a, b) => {
    const pr = (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9);
    if (pr !== 0) return pr;
    return String(lastChangeAt(b)).localeCompare(String(lastChangeAt(a))); // fecha desc (ISO UTC → lexicográfico)
  });
}

// ---- Tarjeta ----
function TicketCard({ ticket, onClick }) {
  const affected = affectedLabel(ticket);
  const urgent = ticket.priority === 'urgente';
  const blocked = ticket.status === 'bloqueado';
  const blockReason = blocked ? blockReasonOf(ticket) : '';
  const borderClass = blocked ? 'border-rose-500/60' : (urgent ? 'border-red-500/50' : 'border-white/5');
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-lg border p-3 space-y-2 transition-colors bg-black/20 hover:bg-black/30 ${borderClass}`}
      data-testid={`ticket-card-${ticket.id}`}
    >
      <p className="text-sm font-medium text-white leading-snug">{ticket.title}</p>
      <div className="flex flex-wrap gap-1.5">
        {blocked && <Pill className="bg-rose-500 text-white border-rose-300"><Lock className="w-2.5 h-2.5 mr-1" /> Bloqueado</Pill>}
        <Pill className={typeBadgeClass(ticket.type)}>{TYPE_LABELS[ticket.type] || ticket.type}</Pill>
        <Pill className={priorityBadgeClass(ticket.priority)}>{PRIORITY_LABELS[ticket.priority] || ticket.priority}</Pill>
      </div>
      {blocked && (
        <div className="flex items-start gap-1.5 rounded-md border border-rose-500/30 bg-rose-500/10 px-2 py-1.5" data-testid={`ticket-card-block-reason-${ticket.id}`}>
          <Lock className="w-3 h-3 text-rose-300 mt-0.5 shrink-0" />
          <p className="text-[11px] text-rose-200 leading-snug">{blockReason || 'Sin motivo registrado'}</p>
        </div>
      )}
      {affected && <p className="text-[11px] text-muted-foreground truncate">Cliente/área: {affected}</p>}
      {ticket.responsible && <p className="text-[11px] text-muted-foreground truncate">Responsable: {ticket.responsible}</p>}
      <p className="text-[10px] text-white/30">Últ. cambio: {fmtDate(lastChangeAt(ticket))}</p>
    </button>
  );
}

// ---- Columna ----
function Column({ status, tickets, onCardClick }) {
  const isDev = status === 'en_desarrollo';
  const count = tickets.length;
  return (
    <div className="flex flex-col w-72 shrink-0" data-testid={`column-${status}`}>
      <div className="flex items-center justify-between px-1 mb-2">
        <h3 className="text-xs font-heading font-semibold uppercase tracking-wider text-white/70">{STATUS_LABELS[status]}</h3>
        <span
          className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${isDev ? 'bg-[#A5D62C]/20 text-[#A5D62C]' : 'bg-white/5 text-muted-foreground'}`}
          data-testid={`column-count-${status}`}
        >
          {count}
        </span>
      </div>
      <div className="flex-1 space-y-2 min-h-[80px] rounded-lg bg-white/[0.02] p-2">
        {count === 0
          ? <p className="text-[11px] text-white/25 text-center py-6">—</p>
          : tickets.map(t => <TicketCard key={t.id} ticket={t} onClick={() => onCardClick(t)} />)}
      </div>
    </div>
  );
}

// ---- Modal: nueva solicitud ----
const EMPTY_FORM = {
  title: '', type: 'fix', description: '', priority: 'media',
  affected_vertical: '', affected_company_id: '', affected_company_name: '', responsible: '',
};

function NewTicketDialog({ open, onOpenChange, onCreated, companies, superadmins }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Cliente: guarda id + name (name derivado del catálogo); vacío limpia ambos.
  const setCompany = (id) => {
    const c = companies.find(x => x.value === id);
    setForm(prev => ({ ...prev, affected_company_id: id, affected_company_name: c ? c.label : '' }));
  };

  useEffect(() => { if (open) setForm(EMPTY_FORM); }, [open]);

  const submit = async () => {
    if (!form.title.trim()) { toast.error('El título es obligatorio'); return; }
    setSaving(true);
    try {
      // description es la caja libre → el backend la copia a intake.raw_request si no mandamos raw_request.
      await api.post('/admin/dev-tickets', {
        title: form.title.trim(),
        type: form.type,
        description: form.description,
        priority: form.priority,
        affected_vertical: form.affected_vertical || null,
        affected_company_id: form.affected_company_id || null,
        affected_company_name: form.affected_company_name || null,
        responsible: form.responsible || null,
      });
      toast.success('Solicitud creada');
      onOpenChange(false);
      onCreated();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'No se pudo crear la solicitud');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[#0f1420] border-white/10 text-white max-h-[88vh] overflow-y-auto" data-testid="new-ticket-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Nueva solicitud</DialogTitle>
          <DialogDescription>Captura mínima. El cuestionario guiado llega en una fase posterior.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Título *">
            <Input value={form.title} onChange={e => set('title', e.target.value)} placeholder="Resumen corto" className="bg-black/20 border-white/10 text-sm h-9" data-testid="new-ticket-title" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tipo">
              <SelectField value={form.type} onChange={v => set('type', v)} options={TYPES} labels={TYPE_LABELS} testid="new-ticket-type" />
            </Field>
            <Field label="Prioridad">
              <SelectField value={form.priority} onChange={v => set('priority', v)} options={PRIORITIES} labels={PRIORITY_LABELS} testid="new-ticket-priority" />
            </Field>
          </div>
          <Field label="Descripción">
            <Textarea value={form.description} onChange={e => set('description', e.target.value)} rows={4} placeholder="Describe la solicitud en tus palabras…" className="bg-black/20 border-white/10 text-sm" data-testid="new-ticket-description" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Vertical afectada">
              <SelectField value={form.affected_vertical} onChange={v => set('affected_vertical', v)} options={VERTICALS} labels={VERTICAL_LABELS} testid="new-ticket-vertical" allowNone />
            </Field>
            <Field label="Cliente afectado">
              <EntitySelect value={form.affected_company_id} onChange={setCompany} items={companies} currentLabel={form.affected_company_name} placeholder="Empresa (opcional)" testid="new-ticket-company" />
            </Field>
          </div>
          <Field label="Responsable">
            <EntitySelect value={form.responsible} onChange={v => set('responsible', v)} items={superadmins} currentLabel={form.responsible} placeholder="Superadmin (opcional)" testid="new-ticket-responsible" />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-muted-foreground">Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#A5D62C] hover:bg-[#95C225] text-black font-bold" data-testid="new-ticket-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
            Crear solicitud
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Modal: detalle + edición + cambio de estado ----
function TicketDetailDialog({ ticket, open, onOpenChange, onChanged, companies, superadmins }) {
  const [edit, setEdit] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [pendingStatus, setPendingStatus] = useState('');
  const [movingStatus, setMovingStatus] = useState(false);
  const [blockNote, setBlockNote] = useState('');

  useEffect(() => {
    if (ticket) {
      setEdit({
        priority: ticket.priority || 'media',
        responsible: ticket.responsible || '',
        affected_vertical: ticket.affected_vertical || '',
        affected_company_id: ticket.affected_company_id || '',
        affected_company_name: ticket.affected_company_name || '',
        description: ticket.description || '',
      });
      setPendingStatus('');
      // Si ya está bloqueado, prellenar con el motivo actual (para poder ACTUALIZARLO sin desbloquear).
      setBlockNote(ticket.status === 'bloqueado' ? blockReasonOf(ticket) : '');
    }
  }, [ticket]);

  if (!ticket || !edit) return null;

  const setE = (k, v) => setEdit(prev => ({ ...prev, [k]: v }));
  const setCompany = (id) => {
    const c = companies.find(x => x.value === id);
    setEdit(prev => ({ ...prev, affected_company_id: id, affected_company_name: c ? c.label : '' }));
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await api.patch(`/admin/dev-tickets/${ticket.id}`, {
        priority: edit.priority,
        responsible: edit.responsible || null,
        affected_vertical: edit.affected_vertical || null,
        affected_company_id: edit.affected_company_id || null,
        affected_company_name: edit.affected_company_name || null,
        description: edit.description,
      });
      toast.success('Cambios guardados');
      onChanged();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'No se pudo guardar');
    } finally {
      setSavingEdit(false);
    }
  };

  const history = ticket.status_history || [];
  const currentReason = blockReasonOf(ticket);            // '' si el ticket no está bloqueado
  const target = pendingStatus || ticket.status;          // estado destino efectivo
  const isBlockTarget = target === 'bloqueado';
  const willNotify = pendingStatus && NOTIFY_STATUSES.includes(pendingStatus);
  const statusChanging = pendingStatus && pendingStatus !== ticket.status;
  // Confirmar habilitado: bloquear → motivo no vacío (y si YA está bloqueado, que el motivo CAMBIE,
  // para no crear eventos duplicados); cualquier otro destino → cambio real de estado.
  const canApplyStatus = isBlockTarget
    ? (blockNote.trim() !== '' && (target !== ticket.status || blockNote.trim() !== (currentReason || '').trim()))
    : statusChanging;
  const applyLabel = isBlockTarget ? (ticket.status === 'bloqueado' ? 'Actualizar motivo' : 'Bloquear') : 'Confirmar';

  const applyStatus = async () => {
    if (!canApplyStatus) return;
    setMovingStatus(true);
    try {
      const body = { status: target };
      if (isBlockTarget) body.note = blockNote.trim();
      await api.patch(`/admin/dev-tickets/${ticket.id}/status`, body);
      toast.success(
        NOTIFY_STATUSES.includes(target)
          ? 'Estado actualizado · aviso enviado al equipo'
          : isBlockTarget
            ? (target !== ticket.status ? 'Ticket bloqueado' : 'Motivo del bloqueo actualizado')
            : 'Estado actualizado'
      );
      setPendingStatus('');
      onChanged();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'No se pudo cambiar el estado');
    } finally {
      setMovingStatus(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#0f1420] border-white/10 text-white max-h-[88vh] overflow-y-auto" data-testid="ticket-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-white pr-6">{ticket.title}</DialogTitle>
          <DialogDescription>Creado por {ticket.created_by || '—'} · {fmtDate(ticket.created_at)}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-1.5">
          <Pill className={typeBadgeClass(ticket.type)}>{TYPE_LABELS[ticket.type] || ticket.type}</Pill>
          <Pill className={priorityBadgeClass(ticket.priority)}>{PRIORITY_LABELS[ticket.priority] || ticket.priority}</Pill>
          <Pill className={ticket.status === 'bloqueado' ? 'bg-rose-500 text-white border-rose-300' : 'bg-white/5 text-white/70 border-white/10'}>{STATUS_LABELS[ticket.status] || ticket.status}</Pill>
        </div>

        {ticket.status === 'bloqueado' && (
          <div className="flex items-start gap-2 rounded-lg border border-rose-500/40 bg-rose-500/10 p-3" data-testid="detail-block-banner">
            <Lock className="w-4 h-4 text-rose-300 mt-0.5 shrink-0" />
            <div>
              <p className="text-[11px] uppercase tracking-wider text-rose-300 font-semibold">Bloqueado</p>
              <p className="text-sm text-rose-100 leading-snug">{currentReason || 'Sin motivo registrado'}</p>
            </div>
          </div>
        )}

        {/* Cambiar estado (selector + motivo si se bloquea + aviso Telegram) */}
        <div className="rounded-lg border border-white/10 bg-black/20 p-3 space-y-2">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Mover a estado</Label>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={pendingStatus || ticket.status} onValueChange={setPendingStatus}>
              <SelectTrigger className="bg-black/20 border-white/10 text-sm h-9 w-48" data-testid="detail-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ALL_STATUSES.map(s => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              onClick={applyStatus}
              disabled={movingStatus || !canApplyStatus}
              className="bg-[#A5D62C] hover:bg-[#95C225] text-black font-bold h-9"
              data-testid="detail-status-apply"
            >
              {movingStatus ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
              {applyLabel}
            </Button>
          </div>
          {isBlockTarget && (
            <div className="space-y-1" data-testid="detail-block-reason-wrap">
              <Label className="text-[10px] uppercase tracking-wider text-rose-300">Motivo del bloqueo *</Label>
              <Textarea
                value={blockNote}
                onChange={e => setBlockNote(e.target.value)}
                rows={2}
                placeholder="¿Por qué se bloquea? (obligatorio)"
                className="bg-black/20 border-rose-500/30 text-sm"
                data-testid="detail-block-reason-input"
              />
            </div>
          )}
          {willNotify && (
            <p className="flex items-center gap-1.5 text-[11px] text-amber-300" data-testid="detail-telegram-warning">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> Al confirmar, esto notifica al equipo por Telegram.
            </p>
          )}
        </div>

        {/* Editar campos (PATCH parcial) */}
        <div className="rounded-lg border border-white/10 bg-black/20 p-3 space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Editar campos</Label>
            <Button onClick={saveEdit} disabled={savingEdit} variant="outline" size="sm" className="border-white/10 h-8 text-xs" data-testid="detail-save-edit">
              {savingEdit ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
              Guardar cambios
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Prioridad">
              <SelectField value={edit.priority} onChange={v => setE('priority', v)} options={PRIORITIES} labels={PRIORITY_LABELS} testid="detail-edit-priority" />
            </Field>
            <Field label="Responsable">
              <EntitySelect value={edit.responsible} onChange={v => setE('responsible', v)} items={superadmins} currentLabel={edit.responsible} placeholder="Superadmin (opcional)" testid="detail-edit-responsible" />
            </Field>
            <Field label="Vertical afectada">
              <SelectField value={edit.affected_vertical} onChange={v => setE('affected_vertical', v)} options={VERTICALS} labels={VERTICAL_LABELS} testid="detail-edit-vertical" allowNone />
            </Field>
            <Field label="Cliente afectado">
              <EntitySelect value={edit.affected_company_id} onChange={setCompany} items={companies} currentLabel={edit.affected_company_name} placeholder="Empresa (opcional)" testid="detail-edit-company" />
            </Field>
          </div>
          <Field label="Descripción">
            <Textarea value={edit.description} onChange={e => setE('description', e.target.value)} rows={4} className="bg-black/20 border-white/10 text-sm" data-testid="detail-edit-description" />
          </Field>
        </div>

        {/* Historial de estados */}
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Historial de estados</Label>
          <div className="space-y-1.5">
            {history.length === 0 && <p className="text-[11px] text-white/30">Sin historial.</p>}
            {[...history].reverse().map((h, i) => (
              <div key={`${h.at}-${i}`} className="text-[11px]">
                <div className="flex flex-wrap items-center gap-2">
                  <Pill className={h.status === 'bloqueado' ? 'bg-rose-500/80 text-white border-rose-300' : 'bg-white/5 text-white/70 border-white/10'}>{STATUS_LABELS[h.status] || h.status}</Pill>
                  <span className="text-muted-foreground">{fmtDate(h.at)}</span>
                  <span className="text-white/40">· {h.by || '—'}</span>
                </div>
                {h.note && <p className="mt-0.5 ml-1 text-rose-200/80 leading-snug">↳ {h.note}</p>}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---- Página ----
export default function DevTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [showOffFlow, setShowOffFlow] = useState(false);
  const [companies, setCompanies] = useState([]);       // [{value:id, label:name}] para el selector de cliente
  const [superadmins, setSuperadmins] = useState([]);   // [{value:name, label:name}] para el selector de responsable

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const r = await api.get('/admin/dev-tickets');
      setTickets(Array.isArray(r.data?.items) ? r.data.items : []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Catálogos para los selectores (no bloquean el tablero; si fallan, los selectores quedan vacíos).
  useEffect(() => {
    api.get('/admin/companies')
      .then(r => setCompanies((Array.isArray(r.data) ? r.data : []).map(c => ({ value: c.id, label: c.name || c.id }))))
      .catch(() => {});
    api.get('/admin/superadmins')
      .then(r => setSuperadmins((Array.isArray(r.data) ? r.data : []).map(s => ({ value: s.name || s.email, label: s.name || s.email })).filter(s => s.value)))
      .catch(() => {});
  }, []);

  // Mantener el ticket abierto en el detalle sincronizado con la data fresca tras un reload.
  useEffect(() => {
    setSelected(prev => (prev ? tickets.find(t => t.id === prev.id) || prev : prev));
  }, [tickets]);

  const byStatus = useMemo(() => {
    const buckets = {};
    ALL_STATUSES.forEach(s => { buckets[s] = []; });
    for (const t of tickets) {
      const s = ALL_STATUSES.includes(t.status) ? t.status : 'solicitado';
      buckets[s].push(t);
    }
    ALL_STATUSES.forEach(s => { buckets[s] = sortTickets(buckets[s]); });
    return buckets;
  }, [tickets]);

  const offFlowCount = OFF_FLOW_STATUSES.reduce((n, s) => n + byStatus[s].length, 0);
  const isEmpty = !loading && !error && tickets.length === 0;

  const openDetail = (t) => { setSelected(t); setDetailOpen(true); };

  return (
    <div data-testid="dev-tickets-page">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-[#A5D62C]" /> Proyectos
          </h2>
          <p className="text-muted-foreground text-xs sm:text-sm mt-1">Tablero de tickets del equipo de desarrollo. Solo Super Admin.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={load} variant="outline" size="sm" className="border-white/10 h-9 text-xs" disabled={loading} data-testid="dev-tickets-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Actualizar
          </Button>
          <Button onClick={() => setNewOpen(true)} className="bg-[#A5D62C] hover:bg-[#95C225] text-black font-bold h-9 text-xs" data-testid="dev-tickets-new">
            <Plus className="w-4 h-4 mr-1.5" /> Nueva solicitud
          </Button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20" data-testid="dev-tickets-loading">
          <Loader2 className="w-6 h-6 text-[#A5D62C] animate-spin" />
        </div>
      )}

      {error && !loading && (
        <div className="flex flex-col items-center justify-center py-20 gap-3" data-testid="dev-tickets-error">
          <AlertTriangle className="w-8 h-8 text-rose-400" />
          <p className="text-sm text-muted-foreground">No se pudieron cargar los tickets.</p>
          <Button onClick={load} variant="outline" size="sm" className="border-white/10">Reintentar</Button>
        </div>
      )}

      {isEmpty && (
        <div className="flex flex-col items-center justify-center py-20 gap-3" data-testid="dev-tickets-empty">
          <ClipboardList className="w-10 h-10 text-white/20" />
          <p className="text-sm text-muted-foreground">Aún no hay tickets.</p>
          <Button onClick={() => setNewOpen(true)} className="bg-[#A5D62C] hover:bg-[#95C225] text-black font-bold text-xs">
            <Plus className="w-4 h-4 mr-1.5" /> Crear la primera solicitud
          </Button>
        </div>
      )}

      {!loading && !error && tickets.length > 0 && (
        <>
          <div className="flex gap-3 overflow-x-auto pb-3" data-testid="dev-tickets-board">
            {FLOW_STATUSES.map(s => <Column key={s} status={s} tickets={byStatus[s]} onCardClick={openDetail} />)}
          </div>

          {offFlowCount > 0 && (
            <div className="mt-4 border-t border-white/5 pt-3">
              <button
                type="button"
                onClick={() => setShowOffFlow(v => !v)}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-white"
                data-testid="offflow-toggle"
              >
                {showOffFlow ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                Fuera de flujo · {OFF_FLOW_STATUSES.map(s => STATUS_LABELS[s]).join(' / ')} ({offFlowCount})
              </button>
              {showOffFlow && (
                <div className="flex gap-3 overflow-x-auto pb-3 mt-3" data-testid="offflow-board">
                  {OFF_FLOW_STATUSES.map(s => <Column key={s} status={s} tickets={byStatus[s]} onCardClick={openDetail} />)}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <NewTicketDialog open={newOpen} onOpenChange={setNewOpen} onCreated={load} companies={companies} superadmins={superadmins} />
      <TicketDetailDialog ticket={selected} open={detailOpen} onOpenChange={setDetailOpen} onChanged={load} companies={companies} superadmins={superadmins} />
    </div>
  );
}
